import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Snackbar,
  Stack,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  UploadFile as UploadFileIcon,
  DeleteOutline as DeleteOutlineIcon,
  OpenInNew as OpenInNewIcon,
  ChevronRight as ChevronRightIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import api from "../services/api";

const initialFilterState = {
  unit: "",
  year: "",
  month: "",
  projectName: "",
  jobNumber: "",
  status: "",
  dpExecutive: "",
  scriptingExecutive: "",
  researchExecutive: "",
  segmentName: "",
};

const STATUS_COLORS = {
  Open: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  "Test Link Delivered": { bg: "#fffbeb", text: "#92400e", border: "#fde68a" },
  "Final Link Delivered": { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  "Launched / Field Live": { bg: "#eef2ff", text: "#3730a3", border: "#c7d2fe" },
  "Fieldwork Complete": { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  "Data QC Done": { bg: "#ecfeff", text: "#155e75", border: "#a5f3fc" },
  "Interim Delivered": { bg: "#fff7ed", text: "#9a3412", border: "#fed7aa" },
  "Final Delivered": { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  Hold: { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" },
  Closed: { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
};

const getStatusColor = (status) => {
  const colors = {
    Open: "info",
    "Test Link Delivered": "warning",
    "Final Link Delivered": "success",
    "Launched / Field Live": "primary",
    "Fieldwork Complete": "secondary",
    "Data QC Done": "info",
    "Interim Delivered": "warning",
    "Final Delivered": "success",
    Hold: "error",
    Closed: "default",
  };
  return colors[status] || "default";
};

const JTProjectList = () => {
  const [projectNames, setProjectNames] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [cascadingNames, setCascadingNames] = useState([]);
  const email = localStorage.getItem("username");

  const [contactPersonsDP, setContactPersonsDP] = useState([]);
  const [contactPersonsScripting, setContactPersonsScripting] = useState([]);
  const [contactPersonsResearch, setContactPersonsResearch] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // QNR / Analysis Plan popover state
  const [attachPopover, setAttachPopover] = useState(null);
  const [attachStatus, setAttachStatus] = useState({ questionnaire: null, analysis_plan: null });
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachUploading, setAttachUploading] = useState(false);
  const attachFileRef = useRef(null);
  const [rescheduleModal, setRescheduleModal] = useState(null); // { project, type: 'qnr'|'ap', currentDate }
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  const [deleteModal, setDeleteModal] = useState(null); // { project }
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem("projectListFilters");
    try {
      return savedFilters ? JSON.parse(savedFilters) : initialFilterState;
    } catch (e) {
      return initialFilterState;
    }
  });

  useEffect(() => {
    sessionStorage.setItem("projectListFilters", JSON.stringify(filters));
  }, [filters]);

  // Fetch contact persons
  useEffect(() => {
    api
      .get("users/names-departments/", {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` },
      })
      .then((resp) => {
        const profiles = resp.data;
        const dpNames = profiles.filter((p) => p.department === "Data Processing").map((p) => p.name);
        const researchNames = profiles.filter((p) => p.department === "Research").map((p) => p.name);
        setContactPersonsDP(dpNames);
        setContactPersonsScripting(dpNames);
        setContactPersonsResearch(researchNames);
      })
      .catch(() => setError("Failed to load contact person data"));
  }, []);

  // Fetch cascading names
  useEffect(() => {
    const fetchCascadingNames = async () => {
      try {
        if (!email) throw new Error("Email not found");
        const response = await api.get(`users/cascading_names/?email=${email}`);
        setCascadingNames(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCascadingNames();
  }, [email]);

  // Fetch project names
  useEffect(() => {
    const fetchProjectNames = async () => {
      setLoading(true);
      setError("");
      try {
        if (cascadingNames.length === 0) { setLoading(false); return; }
        const cascadingNamesParam = cascadingNames.join("&name=");
        const response = await api.get(`api/segments/fetch-segments-by-name/?name=${cascadingNamesParam}`);
        const data = Array.isArray(response.data) ? response.data : (response.data.segments || []);
        const formattedProjectNames = data.map((project) => ({
          ...project,
          actualDateOfFinalOutput: project.actual_date_of_final_output ? moment(project.actual_date_of_final_output).format("YYYY-MM-DD") : null,
          actualDateOfLink: project.actual_date_of_link ? moment(project.actual_date_of_link).format("YYYY-MM-DD") : null,
          expectedDateOfFinalOutput: project.expected_date_of_final_output ? moment(project.expected_date_of_final_output).format("YYYY-MM-DD") : null,
          expectedDateOfLink: project.expected_date_of_link ? moment(project.expected_date_of_link).format("YYYY-MM-DD") : null,
        }));
        setProjectNames(formattedProjectNames);
        setFilteredProjects(formattedProjectNames);
      } catch (err) {
        setError("An error occurred while fetching projects.");
      } finally {
        setLoading(false);
      }
    };
    fetchProjectNames();
  }, [cascadingNames]);

  // Auto-open upload or reschedule dialog when arriving via email link
  useEffect(() => {
    if (projectNames.length === 0) return;

    const uploadQnrId = searchParams.get('upload_qnr');
    const uploadApId  = searchParams.get('upload_ap');
    const reschedQnrId = searchParams.get('reschedule_qnr');
    const reschedApId  = searchParams.get('reschedule_ap');

    const uploadId   = uploadQnrId || uploadApId;
    const uploadType = uploadQnrId ? 'questionnaire' : 'analysis_plan';
    const reschedId  = reschedQnrId || reschedApId;
    const reschedType = reschedQnrId ? 'qnr' : 'ap';

    if (uploadId) {
      const project = projectNames.find(p => String(p.id) === String(uploadId));
      if (project) {
        setAttachPopover({ anchorEl: null, project, type: uploadType });
        setAttachStatus({ questionnaire: null, analysis_plan: null });
        setAttachLoading(true);
        api.get(`api/pinned-attachments/?segment_id=${project.id}`)
          .then(res => setAttachStatus({ questionnaire: res.data.questionnaire || null, analysis_plan: res.data.analysis_plan || null }))
          .catch(() => {})
          .finally(() => setAttachLoading(false));
        setSearchParams({}, { replace: true });
      }
    } else if (reschedId) {
      const project = projectNames.find(p => String(p.id) === String(reschedId));
      if (project) {
        setRescheduleModal({ project, type: reschedType });
        setRescheduleDate('');
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, projectNames]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => setFilters(initialFilterState);

  const handleNavigateToChangeLog = async (project) => {
    try {
      const { job_number, segment_name } = project;
      const response = await api.get(`api/segments/fetch_segment_id_by_job_and_name?job_number=${job_number}&segment_name=${segment_name}`);
      const { segmentId } = response.data;
      if (!segmentId) { alert("Could not find a unique ID for this segment."); return; }
      navigate(`/changelog/${segmentId}`, { state: { project, focus: "changeLog" } });
    } catch (error) {
      alert("An error occurred while trying to open the change log.");
    }
  };

  // Filter + sort
  useEffect(() => {
    const today = moment().startOf("day");
    const getGroup = (date) => {
      if (!date) return 3;
      const diff = moment(date).startOf("day").diff(today, "days");
      if (diff === 0) return 0;
      if (diff > 0) return 1;
      return 2;
    };
    const filtered = projectNames.filter((project) =>
      (filters.unit === "" || (project.unit && project.unit.toLowerCase().includes(filters.unit.toLowerCase()))) &&
      (filters.year === "" || (project.year && project.year.toString().includes(filters.year))) &&
      (filters.month === "" || (project.month && project.month.toLowerCase().includes(filters.month.toLowerCase()))) &&
      (filters.projectName === "" || (project.project_name && project.project_name.toLowerCase().includes(filters.projectName.toLowerCase()))) &&
      (filters.jobNumber === "" || (project.job_number && project.job_number.toLowerCase().includes(filters.jobNumber.toLowerCase()))) &&
      (filters.status === "" || (project.status && project.status.toLowerCase().includes(filters.status.toLowerCase()))) &&
      (filters.dpExecutive === "" || (project.contact_person_for_dp && project.contact_person_for_dp.toLowerCase().includes(filters.dpExecutive.toLowerCase()))) &&
      (filters.scriptingExecutive === "" || (project.contact_person_for_scripting && project.contact_person_for_scripting.toLowerCase().includes(filters.scriptingExecutive.toLowerCase()))) &&
      (filters.researchExecutive === "" || (project.contact_person_in_research && project.contact_person_in_research.toLowerCase().includes(filters.researchExecutive.toLowerCase()))) &&
      (filters.segmentName === "" || (project.segment_name && project.segment_name.toLowerCase().includes(filters.segmentName.toLowerCase())))
    );
    const sorted = filtered.sort((a, b) => {
      const aDate = a.expectedDateOfFinalOutput;
      const bDate = b.expectedDateOfFinalOutput;
      const groupA = getGroup(aDate);
      const groupB = getGroup(bDate);
      if (groupA !== groupB) return groupA - groupB;
      if (groupA === 0 || groupA === 3) return 0;
      if (groupA === 1) return moment(aDate).diff(moment(bDate));
      if (groupA === 2) return moment(bDate).diff(moment(aDate));
      return 0;
    });
    setFilteredProjects(sorted);
    setCurrentPage(1);
  }, [filters, projectNames]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);

  // QNR / AP handlers
  const openAttachPopover = async (e, project, type) => {
    setAttachPopover({ anchorEl: e.currentTarget, project, type });
    setAttachStatus({ questionnaire: null, analysis_plan: null });
    setAttachLoading(true);
    try {
      const res = await api.get(`api/pinned-attachments/?segment_id=${project.id}`);
      setAttachStatus({ questionnaire: res.data.questionnaire || null, analysis_plan: res.data.analysis_plan || null });
    } catch { } finally { setAttachLoading(false); }
  };

  const closeAttachPopover = () => setAttachPopover(null);

  const handleAttachUpload = async (file) => {
    if (!attachPopover || !file) return;
    setAttachUploading(true);
    try {
      const formData = new FormData();
      formData.append("segment_id", attachPopover.project.id);
      formData.append("attachment_type", attachPopover.type);
      formData.append("file", file);
      const res = await api.post("api/pinned-attachments/upload/", formData, {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` },
      });
      setAttachStatus(prev => ({ ...prev, [attachPopover.type]: res.data }));
    } catch { } finally { setAttachUploading(false); }
  };

  const handleAttachDelete = async () => {
    if (!attachPopover) return;
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`api/pinned-attachments/delete/?segment_id=${attachPopover.project.id}&attachment_type=${attachPopover.type}`, {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` },
      });
      setAttachStatus(prev => ({ ...prev, [attachPopover.type]: null }));
    } catch { }
  };

  const handleDiscussion = async (project) => {
    const segmentId = project.id;
    if (!segmentId) { alert("Segment ID not found for this project!"); return; }
    try {
      await api.get(`api/segments/fetch_unread_count?segment_id=${segmentId}&email=${encodeURIComponent(email)}`);
    } catch { }
    navigate("/discussion", { state: { segmentId } });
  };

  const handleDeleteSegment = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      const res = await api.post(`api/segments/${deleteModal.project.id}/delete-segment/`, {}, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      setProjectNames(prev => prev.filter(p => p.id !== deleteModal.project.id));
      setFilteredProjects(prev => prev.filter(p => p.id !== deleteModal.project.id));
      setDeleteModal(null);
      setDeleteConfirmText('');
      setSuccessMessage(res.data?.message || 'Segment deleted successfully.');
      setShowSuccessToast(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete segment. You may not have permission.';
      alert(msg);
    } finally {
      setDeleting(false);
    }
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const theme = useTheme();

  // Current attachment file for popover display
  const currentAttach = attachPopover ? attachStatus[attachPopover.type] : null;
  const attachLabel = attachPopover?.type === "questionnaire" ? "Questionnaire (QNR)" : "Analysis Plan (AP)";

  return (
    <Box sx={{ minHeight: "100vh", background: "#f8fafc", py: 2.5, px: { xs: 1.5, sm: 2 } }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: "#fff", borderRadius: 3, border: "1px solid #e2e8f0",
        px: 3, py: 2, mb: 2,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: "#1e3a5f", letterSpacing: -0.3 }}>
            Job Tracker
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#64748b", mt: 0.2 }}>
            Manage and track all your projects &bull; {filteredProjects.length} project{filteredProjects.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            startIcon={<FilterListIcon fontSize="small" />}
            onClick={() => setShowFilters(v => !v)}
            variant={showFilters ? "contained" : "outlined"}
            disableElevation
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: "0.85rem",
              ...(showFilters
                ? { background: "#eef2ff", color: "#3730a3", border: "1px solid #c7d2fe", "&:hover": { background: "#e0e7ff" } }
                : { borderColor: "#e2e8f0", color: "#64748b", "&:hover": { background: "#f8fafc" } }),
            }}
          >
            Filters
            {activeFilterCount > 0 && (
              <Box component="span" sx={{
                ml: 1, px: 0.8, py: 0.1, borderRadius: 10, background: "#4f46e5",
                color: "#fff", fontSize: "0.7rem", fontWeight: 700, lineHeight: "18px",
              }}>{activeFilterCount}</Box>
            )}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => navigate("/create-new")}
            disableElevation
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: "0.85rem",
              background: "#4f46e5", "&:hover": { background: "#4338ca" },
            }}
          >
            Add New Project
          </Button>
        </Stack>
      </Box>

      {/* ── Collapsible Filters ── */}
      {showFilters && (
        <Card elevation={0} sx={{ mb: 2, borderRadius: 3, border: "1px solid #e2e8f0", background: "#fff" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterListIcon sx={{ color: "#4f46e5", fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#0f172a" }}>Filter Projects</Typography>
              </Stack>
              <Button
                size="small" startIcon={<ClearIcon fontSize="small" />} onClick={handleClearFilters}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", color: "#64748b" }}
              >
                Clear all
              </Button>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField fullWidth label="Year" name="year" value={filters.year} onChange={handleFilterChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select name="month" value={filters.month} onChange={handleFilterChange} label="Month">
                    <MenuItem value="">All Months</MenuItem>
                    {["January","February","March","April","May","June","July","August","September","October","November","December"].map(m => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <TextField fullWidth label="Project Name" name="projectName" value={filters.projectName} onChange={handleFilterChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth label="Segment Name" name="segmentName" value={filters.segmentName} onChange={handleFilterChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth label="Job Number" name="jobNumber" value={filters.jobNumber} onChange={handleFilterChange} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select name="status" value={filters.status} onChange={handleFilterChange} label="Status">
                    <MenuItem value="">All Statuses</MenuItem>
                    {["Open","Test Link Delivered","Final Link Delivered","Launched / Field Live","Fieldwork Complete","Data QC Done","Interim Delivered","Final Delivered","Hold","Closed"].map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>DP Executive</InputLabel>
                  <Select name="dpExecutive" value={filters.dpExecutive} onChange={handleFilterChange} label="DP Executive">
                    <MenuItem value="">Any DP</MenuItem>
                    {contactPersonsDP.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Scripting Executive</InputLabel>
                  <Select name="scriptingExecutive" value={filters.scriptingExecutive} onChange={handleFilterChange} label="Scripting Executive">
                    <MenuItem value="">Any Scripter</MenuItem>
                    {contactPersonsScripting.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Research Executive</InputLabel>
                  <Select name="researchExecutive" value={filters.researchExecutive} onChange={handleFilterChange} label="Research Executive">
                    <MenuItem value="">Any Researcher</MenuItem>
                    {contactPersonsResearch.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
          <CircularProgress size={36} sx={{ color: "#4f46e5" }} />
          <Typography sx={{ ml: 2, color: "#64748b" }}>Loading projects…</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : filteredProjects.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>No projects found.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{
            borderRadius: 3, border: "1px solid #e2e8f0", background: "#fff",
            boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            maxHeight: "calc(100vh - 200px)", overflowY: "auto",
          }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-head": { background: "#f8fafc", fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e2e8f0", py: 1.2 } }}>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell>Project</TableCell>
                  <TableCell sx={{ width: "8%" }}>Job Number</TableCell>
                  <TableCell sx={{ width: "14%" }}>Segment</TableCell>
                  <TableCell sx={{ width: "9%" }}>Expected Output</TableCell>
                  <TableCell sx={{ width: "9%" }}>Actual Output</TableCell>
                  <TableCell sx={{ width: "11%" }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: "13%" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((project, index) => {
                  const isExpanded = expandedRow === (indexOfFirstItem + index);

                  const finalOutputStyle =
                    project.actualDateOfFinalOutput && project.expectedDateOfFinalOutput
                      ? moment(project.actualDateOfFinalOutput).isAfter(project.expectedDateOfFinalOutput)
                        ? "#ef4444"
                        : "#16a34a"
                      : "#334155";

                  const finalLinkStyle =
                    project.actualDateOfLink && project.expectedDateOfLink
                      ? moment(project.actualDateOfLink).isAfter(project.expectedDateOfLink)
                        ? "#ef4444"
                        : "#16a34a"
                      : "#334155";

                  const isToday = (date) => moment(date).isSame(moment(), "day");
                  const highlightRow = isToday(project.expectedDateOfFinalOutput) || isToday(project.expectedDateOfLink);

                  const sc = STATUS_COLORS[project.status] || STATUS_COLORS["Closed"];

                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        hover
                        sx={{
                          bgcolor: highlightRow ? "#fffbeb" : "inherit",
                          borderBottom: isExpanded ? "none" : undefined,
                          "& .MuiTableCell-root": { borderBottom: isExpanded ? "none" : "1px solid #f1f5f9", py: 1 },
                          "&:hover": { bgcolor: highlightRow ? "#fef9c3" : "#f8fafc" },
                        }}
                      >
                        {/* Expand chevron */}
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(isExpanded ? null : (indexOfFirstItem + index))}
                            sx={{ color: "#94a3b8", p: 0.5, "&:hover": { color: "#4f46e5" } }}
                          >
                            <ChevronRightIcon sx={{
                              fontSize: 18,
                              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }} />
                          </IconButton>
                        </TableCell>

                        {/* Project name + unit/month/year */}
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#0f172a", lineHeight: 1.3 }}>
                            {project.project_name || "-"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", mt: 0.2 }}>
                            {[project.unit, project.month, project.year].filter(Boolean).join(" · ")}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ fontSize: "0.8rem", color: "#475569" }}>{project.job_number || "-"}</TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", color: "#334155" }}>{project.segment_name || "-"}</Typography>
                          {project.no_of_segments && (
                            <Typography component="span" sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>seg {project.no_of_segments}</Typography>
                          )}
                        </TableCell>

                        <TableCell sx={{ fontSize: "0.8rem", color: finalOutputStyle, fontWeight: finalOutputStyle !== "#334155" ? 600 : 400 }}>
                          {project.expectedDateOfFinalOutput || "-"}
                        </TableCell>

                        <TableCell sx={{ fontSize: "0.8rem", color: finalOutputStyle, fontWeight: finalOutputStyle !== "#334155" ? 600 : 400 }}>
                          {project.actualDateOfFinalOutput || "-"}
                        </TableCell>

                        <TableCell>
                          <Box sx={{
                            display: "inline-flex", px: 1.2, py: 0.4, borderRadius: 10,
                            border: `1px solid ${sc.border}`, background: sc.bg,
                            fontSize: "0.68rem", fontWeight: 600, color: sc.text,
                            whiteSpace: "nowrap",
                          }}>
                            {project.status || "-"}
                          </Box>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.3} justifyContent="flex-end" alignItems="center">
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => navigate("/project_detailed_view", { state: { project } })}
                                sx={{ color: "#94a3b8", p: 0.6, "&:hover": { color: "#4f46e5", bgcolor: "#eef2ff" }, borderRadius: 1 }}>
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Project">
                              <IconButton size="small" onClick={() => navigate("/edit", { state: { jobNumber: project.job_number, editMode: true, segmentName: project.segment_name } })}
                                sx={{ color: "#94a3b8", p: 0.6, "&:hover": { color: "#d97706", bgcolor: "#fffbeb" }, borderRadius: 1 }}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Discussion">
                              <IconButton size="small" onClick={() => handleDiscussion(project)}
                                sx={{ color: "#94a3b8", p: 0.6, "&:hover": { color: "#0891b2", bgcolor: "#ecfeff" }, borderRadius: 1 }}>
                                <Badge badgeContent={project.unread_count || 0} color="error" sx={{ "& .MuiBadge-badge": { fontSize: "0.55rem", minWidth: 14, height: 14 } }}>
                                  <CommentIcon sx={{ fontSize: 16 }} />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Change Log">
                              <IconButton size="small" onClick={() => handleNavigateToChangeLog(project)}
                                sx={{ color: "#94a3b8", p: 0.6, "&:hover": { color: "#7c3aed", bgcolor: "#f5f3ff" }, borderRadius: 1 }}>
                                <AssignmentIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={attachStatus.questionnaire ? "QNR uploaded" : "Upload QNR"}>
                              <IconButton size="small" onClick={e => openAttachPopover(e, project, "questionnaire")}
                                sx={{
                                  color: attachStatus.questionnaire ? "#4a148c" : "#94a3b8",
                                  bgcolor: attachStatus.questionnaire ? "#f3e5f5" : "transparent",
                                  fontWeight: 800, fontSize: "0.55rem", p: 0.6,
                                  "&:hover": { bgcolor: "#f3e5f5", color: "#4a148c" }, borderRadius: 1,
                                }}>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.58rem", lineHeight: 1, color: "inherit" }}>QNR</Typography>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={attachStatus.analysis_plan ? "Analysis Plan uploaded" : "Upload Analysis Plan"}>
                              <IconButton size="small" onClick={e => openAttachPopover(e, project, "analysis_plan")}
                                sx={{
                                  color: attachStatus.analysis_plan ? "#bf360c" : "#94a3b8",
                                  bgcolor: attachStatus.analysis_plan ? "#fbe9e7" : "transparent",
                                  fontWeight: 800, fontSize: "0.55rem", p: 0.6,
                                  "&:hover": { bgcolor: "#fbe9e7", color: "#bf360c" }, borderRadius: 1,
                                }}>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.58rem", lineHeight: 1, color: "inherit" }}>AP</Typography>
                              </IconButton>
                            </Tooltip>
                            {(() => {
                              const myName = (cascadingNames[0] || '').toLowerCase().trim();
                              const scripting = (project.contact_person_for_scripting || '').toLowerCase().trim();
                              const dp = (project.contact_person_for_dp || '').toLowerCase().trim();
                              const canDelete = myName && (myName === scripting || myName === dp);
                              return (
                                <Tooltip title={canDelete ? "Delete Segment" : "Only the scripting or DP contact can delete"}>
                                  <span>
                                    <IconButton size="small"
                                      disabled={!canDelete}
                                      onClick={() => { setDeleteModal({ project }); setDeleteConfirmText(''); }}
                                      sx={{ color: canDelete ? "#94a3b8" : "#d1d5db", p: 0.6, "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" }, borderRadius: 1 }}>
                                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <TableRow sx={{ "& .MuiTableCell-root": { borderBottom: "1px solid #e2e8f0" } }}>
                          <TableCell colSpan={8} sx={{ background: "#f8fafc", px: 6, py: 2.5 }}>
                            <Grid container spacing={3}>
                              <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Research Contact</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#334155" }}>{project.contact_person_in_research || "-"}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Scripting Contact</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#334155" }}>{project.contact_person_for_scripting || "-"}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>DP Contact</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#334155" }}>{project.contact_person_for_dp || "-"}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Unit</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#334155" }}>{project.unit || "-"}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <LinkIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>Expected Final Link</Typography>
                                  <Typography sx={{ fontSize: "0.82rem", color: finalLinkStyle, fontWeight: finalLinkStyle !== "#334155" ? 600 : 400 }}>
                                    {project.expectedDateOfLink || "-"}
                                  </Typography>
                                </Stack>
                              </Grid>
                              <Grid item xs={6}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <LinkIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>Actual Final Link</Typography>
                                  <Typography sx={{ fontSize: "0.82rem", color: finalLinkStyle, fontWeight: finalLinkStyle !== "#334155" ? 600 : 400 }}>
                                    {project.actualDateOfLink || "-"}
                                  </Typography>
                                </Stack>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
            <TablePagination
              component="div"
              count={filteredProjects.length}
              page={currentPage - 1}
              onPageChange={(_, newPage) => setCurrentPage(newPage + 1)}
              rowsPerPage={itemsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff",
                "& .MuiTablePagination-toolbar": { minHeight: 44 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8rem" },
              }}
            />
          </Box>
        </>
      )}

      {/* ── QNR / AP Popover (button-triggered) ── */}
      <Popover
        open={Boolean(attachPopover) && Boolean(attachPopover?.anchorEl)}
        anchorEl={attachPopover?.anchorEl}
        onClose={closeAttachPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.10)", minWidth: 260 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a", mb: 1.5 }}>{attachLabel}</Typography>
          {attachLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
          ) : currentAttach ? (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, p: 1, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ flex: 1, fontSize: "0.78rem", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {currentAttach.filename || currentAttach.file?.split("/").pop() || "Attached file"}
                </Typography>
                <Tooltip title="Open file">
                  <IconButton size="small" component="a" href={currentAttach.file} target="_blank" rel="noopener noreferrer" sx={{ color: "#4f46e5" }}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" startIcon={<UploadFileIcon fontSize="small" />}
                  onClick={() => attachFileRef.current?.click()}
                  disabled={attachUploading}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderRadius: 1.5, flex: 1 }}>
                  Replace
                </Button>
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon fontSize="small" />}
                  onClick={handleAttachDelete}
                  disabled={attachUploading}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderRadius: 1.5 }}>
                  Remove
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", mb: 1.5 }}>No file uploaded yet.</Typography>
              <Button size="small" variant="contained" startIcon={<UploadFileIcon fontSize="small" />}
                onClick={() => attachFileRef.current?.click()}
                disabled={attachUploading}
                disableElevation
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderRadius: 1.5, background: "#4f46e5", "&:hover": { background: "#4338ca" }, width: "100%" }}>
                {attachUploading ? "Uploading…" : "Upload File"}
              </Button>
            </Box>
          )}
          <input
            ref={attachFileRef}
            type="file"
            style={{ display: "none" }}
            onChange={e => { if (e.target.files[0]) handleAttachUpload(e.target.files[0]); e.target.value = ""; }}
          />
        </Box>
      </Popover>

      {/* ── QNR Upload Dialog (email link-triggered) ── */}
      <Dialog
        open={Boolean(attachPopover) && !attachPopover?.anchorEl}
        onClose={closeAttachPopover}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ background: '#0f172a', color: '#fff', fontWeight: 700, fontSize: '0.95rem', py: 2, px: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileIcon sx={{ fontSize: 18, color: '#4ade80' }} />
          Upload Questionnaire
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {attachPopover?.project && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.3 }}>Project</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>{attachPopover.project.project_name}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{attachPopover.project.job_number}</Typography>
            </Box>
          )}
          {attachLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : currentAttach ? (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0' }}>
                <Typography sx={{ flex: 1, fontSize: '0.8rem', color: '#15803d', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ✓ {currentAttach.filename || currentAttach.file?.split('/').pop() || 'Uploaded'}
                </Typography>
                <IconButton size="small" component="a" href={currentAttach.file} target="_blank" rel="noopener noreferrer" sx={{ color: '#16a34a' }}>
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              <Button fullWidth variant="outlined" startIcon={<UploadFileIcon />} onClick={() => attachFileRef.current?.click()} disabled={attachUploading}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
                Replace File
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mb: 2 }}>
                No questionnaire has been uploaded for this project yet. Please upload the file below.
              </Typography>
              <Button fullWidth variant="contained" startIcon={<UploadFileIcon />} onClick={() => attachFileRef.current?.click()} disabled={attachUploading} disableElevation
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, background: '#16a34a', '&:hover': { background: '#15803d' }, py: 1.2, fontSize: '0.88rem' }}>
                {attachUploading ? 'Uploading…' : 'Choose & Upload File'}
              </Button>
            </Box>
          )}
          <input ref={attachFileRef} type="file" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) handleAttachUpload(e.target.files[0]); e.target.value = ''; }} />
        </DialogContent>
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={closeAttachPopover} size="small" sx={{ textTransform: 'none', color: '#94a3b8' }}>Close</Button>
        </Box>
      </Dialog>

      {/* ── Reschedule QNR / AP Dialog (email link-triggered) ── */}
      <Dialog
        open={Boolean(rescheduleModal)}
        onClose={() => setRescheduleModal(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ background: '#0f172a', color: '#fff', fontWeight: 700, fontSize: '0.95rem', py: 2, px: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          Update Expected Date
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {rescheduleModal?.project && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.3 }}>Project</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>{rescheduleModal.project.project_name}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{rescheduleModal.project.job_number} · {rescheduleModal.type === 'qnr' ? 'Questionnaire' : 'Analysis Plan'}</Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: '0.82rem', color: '#475569', mb: 1.5 }}>
            Enter the new expected date. This will update the AWN record and be noted in the project discussion.
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="New Expected Date"
            value={rescheduleDate}
            onChange={e => setRescheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setRescheduleModal(null)} size="small" sx={{ textTransform: 'none', color: '#94a3b8' }}>Cancel</Button>
          <Button
            variant="contained"
            size="small"
            disabled={!rescheduleDate || rescheduleSaving}
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, background: '#4f46e5', '&:hover': { background: '#4338ca' } }}
            onClick={async () => {
              if (!rescheduleModal || !rescheduleDate) return;
              setRescheduleSaving(true);
              try {
                await api.post('api/awn/update-awn-date/', {
                  segment_id: rescheduleModal.project.id,
                  type: rescheduleModal.type,
                  date: rescheduleDate,
                }, { headers: { Authorization: `Token ${localStorage.getItem('Token')}` } });
                setRescheduleModal(null);
                setRescheduleDate('');
                setSuccessMessage('Date updated and noted in the project discussion.');
                setShowSuccessToast(true);
              } catch {
                alert('Failed to update date. Please try again.');
              } finally {
                setRescheduleSaving(false);
              }
            }}
          >
            {rescheduleSaving ? 'Saving…' : 'Update Date'}
          </Button>
        </Box>
      </Dialog>

      {/* Delete Segment Modal */}
      {deleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#7f1d1d,#dc2626)', padding: '20px 24px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>⚠️</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Delete Segment</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 2 }}>This action cannot be undone</div>
              </div>
            </div>
            {/* Body */}
            <div style={{ padding: '20px 24px' }}>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#991b1b', marginBottom: 2 }}>{deleteModal.project.project_name}</div>
                <div style={{ fontSize: 12, color: '#b91c1c' }}>{deleteModal.project.job_number} · {deleteModal.project.segment_name}</div>
              </div>
              <p style={{ fontSize: 13, color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
                Permanently deletes this segment along with <strong>all discussions, files, change logs, and linked records</strong>. This cannot be recovered.
              </p>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>
                Type the project name to confirm: <span style={{ color: '#dc2626' }}>{deleteModal.project.project_name}</span>
              </label>
              <input
                autoFocus
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && deleteConfirmText === deleteModal.project.project_name && !deleting) handleDeleteSegment(); }}
                placeholder="Type project name here…"
                style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${deleteConfirmText === deleteModal.project.project_name ? '#dc2626' : '#d1d5db'}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
              />
            </div>
            {/* Footer */}
            <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }} disabled={deleting}
                style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                onClick={handleDeleteSegment}
                disabled={deleteConfirmText !== deleteModal.project.project_name || deleting}
                style={{ background: deleteConfirmText === deleteModal.project.project_name ? '#dc2626' : '#fca5a5', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: deleteConfirmText === deleteModal.project.project_name ? 'pointer' : 'not-allowed', color: '#fff', fontFamily: 'inherit', transition: 'background 0.2s' }}>
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success snackbar */}
      <Snackbar open={showSuccessToast} autoHideDuration={3000} onClose={() => setShowSuccessToast(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success" onClose={() => setShowSuccessToast(false)} sx={{ borderRadius: 2 }}>{successMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default JTProjectList;
